import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ManualDippingService } from '../manual-dipping.service';
import { DialogRef } from '@angular/cdk/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MDTaskCreateDTO, Source } from '@helpers/types/models';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SourcesService } from '../../sources/sources.service';
import { BehaviorSubject, debounceTime, startWith, tap } from 'rxjs';
import { CustomValidators, FILES_SERVICE_INJECTOR } from '@campaign-portal/components-library';
import { FileInfo } from '@campaign-portal/namespace/common/fileInfo';
import { DnisFilesService } from './dnis-files.service';
import { AdditionalValidators } from '@helpers/shared/validators';

@Component({
	selector: 'app-create-manual-dipping',
	standalone: false,
	templateUrl: './create-manual-dipping.component.html',
	styleUrl: './create-manual-dipping.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [{ provide: FILES_SERVICE_INJECTOR, useExisting: DnisFilesService }]
})
export class CreateManualDippingComponent implements OnInit {
	readonly errors: { key: string; value: string }[] = [
		{ key: 'mask', value: 'gl.errors.invalid_format' },
		{ key: 'required', value: 'gl.errors.required' }
	];
	readonly mdTask: MDTaskCreateDTO = {
		Sources: [],
		NoCache: true,
		Numbers: []
	};

	mdTaskForm = new FormGroup({
		Sources: new FormControl<string[]>([], {
			nonNullable: true,
			validators: [Validators.required, CustomValidators.requiredArrayOrNull]
		}),
		NoCache: new FormControl<boolean>(true, { nonNullable: true }),
		Numbers: new FormControl<string>('', {
			nonNullable: true,
			validators: [Validators.required, AdditionalValidators.elementString(AdditionalValidators.e164Regex)]
		})
	});

	sourcesFilter = new FormControl('');
	filteredSources$ = new BehaviorSubject<Source[]>([]);

	files: FileInfo[] = [];

	private readonly destroyRef = inject(DestroyRef);

	constructor(
		public readonly manualDippingService: ManualDippingService,
		private readonly dialogRef: DialogRef<boolean>,
		readonly sources: SourcesService,
		public readonly dnisFilesService: DnisFilesService
	) {}

	ngOnInit(): void {
		this.sourcesFilter.valueChanges
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				startWith(''),
				debounceTime(100),
				tap((value) => {
					this.filteredSources$.next(
						this.sources.list.filter((el) => el?.Name.toLowerCase().includes(value?.toLowerCase() ?? ''))
					);
				})
			)
			.subscribe();
		this.dnisFilesService.fileData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
			if (res) {
				if (this.mdTaskForm.controls.Numbers.getRawValue()) {
					this.mdTaskForm.controls.Numbers.patchValue(
						[this.mdTaskForm.controls.Numbers.getRawValue(), ...res].join('\n')
					);
				} else {
					this.mdTaskForm.controls.Numbers.patchValue(res.join('\n'));
				}
			}
		});
	}

	confirm(): void {
		const { Sources, NoCache, Numbers } = this.mdTaskForm.getRawValue();
		const param = {
			Sources,
			NoCache,
			Numbers: Numbers.split('\n')
				.map((value) => value.trim())
				.filter(Boolean)
		};
		this.manualDippingService
			.create(param)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp) => {
				if (resp.data.mdTaskID) {
					this.close(true);
				}
			});
	}

	close(result?: any): void {
		this.mdTaskForm.reset();
		this.dnisFilesService.fileData.next(null);
		this.dnisFilesService.uploadError.next(false);
		this.dialogRef.close(result);
	}
}
